import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  MapPin,
  Phone,
  WhatsappLogo,
  CheckCircle,
  Sparkle,
  Lock,
} from '@phosphor-icons/react';

/**
 * Page teaser publique pour SADASkinCare / SADAHealth / Conseil Bébé.
 *
 * Principe : NE DONNE PAS accès à l'outil métier interne — montre uniquement
 * la promesse d'expérience pour inciter le client à venir en officine.
 *
 * Props :
 *   - kind : 'skincare' | 'health' | 'bebe' (préset de contenu)
 *   - onBack : callback retour vers la home publique
 *   - phone : numéro à appeler (avec préfixe pays)
 *   - whatsapp : numéro WhatsApp sans le +
 *   - mapsQuery : chaîne pour Google Maps deep link
 */
const PRESETS = {
  skincare: {
    image: '/images/teasing_skincare.png',
    accent: '#5B3B8C', // violet SkinCare
    accentSoft: '#EDE7F6',
    badge: 'SADASkinCare · Diagnostic peau',
    title: 'Problèmes de peau ? Ne choisissez plus vos produits au hasard.',
    accroche:
      "Dans notre pharmacie, nous utilisons un outil digital pour analyser votre peau et vous proposer une routine vraiment adaptée — pas un produit pris au hasard.",
    experience: [
      'Analyse précise de votre type de peau',
      "Identification de vos problèmes (acné, taches, irritation, sécheresse…)",
      "Routine personnalisée jour & nuit",
      'Conseils adaptés à votre budget',
    ],
    result:
      "Vous repartez avec des produits VRAIMENT adaptés à votre peau — plus de gaspillage, plus de mauvaises surprises.",
    onlyPharmacy: 'Diagnostic disponible uniquement en pharmacie',
    quote: {
      text:
        "« J'ai testé plein de crèmes avant — depuis le diagnostic, j'ai enfin trouvé ce qui marche pour ma peau. »",
      author: 'Aïcha, 28 ans',
    },
  },
  health: {
    image: '/images/teasing_health.png',
    accent: '#0E7490', // bleu Health
    accentSoft: '#E0F2FE',
    badge: 'SADAHealth · Suivi santé',
    title: 'Suivez votre santé simplement, en pharmacie.',
    accroche:
      'Nous vous aidons à contrôler vos paramètres de santé et à mieux comprendre votre état — sans rendez-vous, sans attente.',
    experience: [
      'Contrôle de la tension artérielle',
      'Mesure de la glycémie',
      "Calcul de l'IMC",
      'Conseils personnalisés du pharmacien',
    ],
    result:
      "En quelques minutes, vous savez où vous en êtes — et nous vous guidons pour la suite.",
    onlyPharmacy: 'Mesures disponibles uniquement en pharmacie',
    quote: {
      text:
        "« Je passe chaque mois faire ma tension. C'est rapide, gratuit, et le pharmacien prend le temps de m'expliquer. »",
      author: 'Monsieur Konan, 64 ans',
    },
  },
  bebe: {
    image: '/images/teasing_bebe.png',
    accent: '#DB2777', // rose bébé
    accentSoft: '#FCE7F3',
    badge: 'Conseil Bébé · Espace dédié',
    title: 'Votre bébé mérite des conseils sur mesure.',
    accroche:
      "Maman pour la 1ère fois ou expérimentée, nos pharmaciens vous accompagnent pour les choix essentiels du quotidien : peau, sommeil, alimentation, premiers soins.",
    experience: [
      'Conseils sur la peau et le change',
      "Aide au choix du lait & compléments",
      "Premiers soins & trousse maison",
      'Dépistage des signes qui doivent alerter',
    ],
    result:
      "Vous repartez rassurée, avec des produits adaptés à l'âge de votre bébé et une oreille attentive.",
    onlyPharmacy: 'Conseil disponible uniquement en pharmacie',
    quote: {
      text:
        "« On m'a guidée comme si j'étais en famille. J'y reviens pour chaque question sur ma fille. »",
      author: 'Bénédicte, jeune maman',
    },
  },
};

const TeaserPage = ({
  kind = 'skincare',
  onBack,
  phone = '+22507784152 77'.replace(/\s/g, ''),
  whatsapp = '2250709060203',
  mapsQuery = 'Pharmacie Saint Ange Des Amis Cocody Angré 7e tranche Abidjan',
}) => {
  const preset = PRESETS[kind] || PRESETS.skincare;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    mapsQuery,
  )}`;
  const waText = encodeURIComponent(
    `Bonjour, je vous écris depuis votre site. Je souhaite venir pour : ${preset.badge}.`,
  );
  const waUrl = `https://wa.me/${whatsapp.replace(/\D/g, '')}?text=${waText}`;
  const telUrl = `tel:${phone.replace(/\s/g, '')}`;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header simple */}
      <header className="bg-white/80 backdrop-blur border-b border-zinc-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="hover:bg-zinc-100 rounded-xl"
            data-testid="teaser-back-btn"
          >
            <ArrowLeft size={22} weight="bold" />
          </Button>
          <span
            className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full"
            style={{ background: preset.accentSoft, color: preset.accent }}
          >
            {preset.badge}
          </span>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-8 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid md:grid-cols-2 gap-8 items-center"
        >
          <div>
            <h1
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 text-zinc-900"
              data-testid="teaser-title"
            >
              {preset.title}
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed mb-6">
              {preset.accroche}
            </p>
            {/* Frustration positive */}
            <div
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full mb-6"
              style={{ background: preset.accentSoft, color: preset.accent }}
            >
              <Lock size={14} weight="fill" />
              {preset.onlyPharmacy}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-zinc-200">
            <img
              src={preset.image}
              alt={preset.badge}
              className="w-full h-full object-cover aspect-[16/10]"
              data-testid="teaser-hero-image"
            />
          </div>
        </motion.div>
      </section>

      {/* Ce que vous allez vivre */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <h2 className="text-zinc-500 text-xs uppercase tracking-wider font-semibold mb-3">
          Ce que vous allez vivre
        </h2>
        <ul className="grid sm:grid-cols-2 gap-3">
          {preset.experience.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 bg-white border border-zinc-100 rounded-xl px-4 py-3 shadow-sm"
            >
              <CheckCircle
                size={20}
                weight="fill"
                className="flex-shrink-0 mt-0.5"
                style={{ color: preset.accent }}
              />
              <span className="text-zinc-800">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Résultat attendu */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <div
          className="rounded-2xl p-6 sm:p-8 border-2"
          style={{ borderColor: preset.accent, background: preset.accentSoft }}
        >
          <div className="flex items-start gap-3">
            <Sparkle
              size={28}
              weight="duotone"
              className="flex-shrink-0 mt-1"
              style={{ color: preset.accent }}
            />
            <div>
              <h2 className="text-zinc-500 text-xs uppercase tracking-wider font-semibold mb-2">
                Résultat attendu
              </h2>
              <p
                className="text-lg sm:text-xl font-medium leading-relaxed"
                style={{ color: preset.accent }}
              >
                {preset.result}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mini-témoignage */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <blockquote
          className="bg-white border-l-4 rounded-r-xl px-5 py-4 italic text-zinc-700 shadow-sm"
          style={{ borderColor: preset.accent }}
        >
          {preset.quote.text}
          <footer className="mt-2 not-italic text-sm text-zinc-500">
            — {preset.quote.author}
          </footer>
        </blockquote>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div
          className="rounded-2xl p-6 sm:p-8 text-white"
          style={{ background: preset.accent }}
        >
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">
            Passez nous voir en pharmacie
          </h2>
          <p className="text-white/85 mb-6 text-base">
            Pharmacie Saint Ange Des Amis · Cocody Angré 7<sup>e</sup> tranche
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-white hover:bg-zinc-100 transition rounded-xl px-4 py-3 font-semibold shadow-md"
              style={{ color: preset.accent }}
              data-testid="teaser-cta-itineraire"
            >
              <MapPin size={20} weight="fill" />
              Itinéraire
            </a>
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ea855] transition rounded-xl px-4 py-3 font-semibold text-white shadow-md"
              data-testid="teaser-cta-whatsapp"
            >
              <WhatsappLogo size={20} weight="fill" />
              WhatsApp
            </a>
            <a
              href={telUrl}
              className="flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur transition rounded-xl px-4 py-3 font-semibold text-white shadow-md border border-white/30"
              data-testid="teaser-cta-appeler"
            >
              <Phone size={20} weight="fill" />
              Appeler
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeaserPage;
